/**
 * BatchesTable — one pipeline's batches, newest first. Each row carries
 * what you open the list to check: where the batch is, and how many of
 * its files are done, skipped or failed.
 */

import { Link } from 'react-router-dom'

import type { BatchResponse } from '@/api/generated/model'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatRelativeTime } from '@/lib/utils'

import { BatchStatusBadge } from './BatchStatusBadge'
import { batchPath } from './paths'

/** "497 of 500 · 3 failed", or the null placeholder before enumeration. */
function ItemCounts({ batch }: { batch: BatchResponse }): React.JSX.Element {
  if (batch.item_total === 0) {
    return <span className="text-muted-foreground">—</span>
  }
  return (
    <span className="font-mono text-xs tabular-nums">
      {batch.item_done.toLocaleString()} of {batch.item_total.toLocaleString()}
      {batch.item_skipped > 0 && (
        <span className="text-muted-foreground">
          {' · '}
          {batch.item_skipped.toLocaleString()} skipped
        </span>
      )}
      {batch.item_failed > 0 && (
        <span className="text-destructive">
          {' · '}
          {batch.item_failed.toLocaleString()} failed
        </span>
      )}
    </span>
  )
}

export function BatchesTable({ batches }: { batches: BatchResponse[] }): React.JSX.Element {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Status</TableHead>
          <TableHead>Input path</TableHead>
          <TableHead className="hidden md:table-cell">Items</TableHead>
          <TableHead className="hidden text-right md:table-cell">Created (newest first)</TableHead>
          <TableHead className="hidden text-right lg:table-cell">Finished</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {batches.map((batch) => (
          <TableRow key={batch.id}>
            <TableCell>
              <Link to={batchPath(batch)} aria-label={`Open batch ${batch.id}`}>
                <BatchStatusBadge status={batch.status} />
              </Link>
            </TableCell>
            <TableCell className="max-w-[28rem]">
              <Link
                to={batchPath(batch)}
                className="block truncate font-mono text-xs text-foreground hover:underline"
                title={batch.input_path}
              >
                {batch.input_path}
              </Link>
            </TableCell>
            <TableCell className="hidden md:table-cell">
              <ItemCounts batch={batch} />
            </TableCell>
            <TableCell className="hidden text-right text-sm text-muted-foreground md:table-cell">
              {formatRelativeTime(batch.created_at)}
            </TableCell>
            <TableCell className="hidden text-right text-sm text-muted-foreground lg:table-cell">
              {batch.finished_at === null ? '—' : formatRelativeTime(batch.finished_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
